<template>
    <v-dialog v-model="dialog" fullscreen hide-overlay transition="dialog-bottom-transition" scrollable persistent
        @keydown.esc="chiudi()">
        <v-card style="height: 100%; display: flex; flex-direction: column;">
            <v-toolbar elevation="0" color="primary" style="flex: 0 0 auto;">
                <v-icon color="white" class="mr-2">person</v-icon>
                <span class="text-h6" style="color: white">{{ titoloScheda }}</span>
                <v-spacer />
                <v-btn icon @click="chiudi()">
                    <v-icon color="white">close</v-icon>
                </v-btn>
            </v-toolbar>

            <v-progress-linear v-if="loading" color="primary" indeterminate style="flex: 0 0 auto;" />

            <v-card-text class="pa-0" style="flex: 1 1 auto; overflow-y: auto; min-height: 0;">
                <v-tabs v-model="activeTab" color="primary" slider-color="primary" show-arrows>
                    <v-tab :disabled="loading">
                        <v-icon left small>badge</v-icon> Dati utente
                    </v-tab>
                    <v-tab :disabled="loading">
                        <v-icon left small>verified</v-icon> Certificazioni
                    </v-tab>
                    <v-tab :disabled="!Cliente.certificazioneFSCG || loading">
                        <v-icon left small>groups</v-icon> FSC gruppo
                    </v-tab>
                    <v-tab :disabled="!Cliente.certificazionePEFCG || loading">
                        <v-icon left small>groups</v-icon> PEFC gruppo
                    </v-tab>
                    <v-tab v-if="!isAuditor" :disabled="loading">
                        <v-icon left small>settings</v-icon> Altri dati
                    </v-tab>
                    <v-tab v-if="!isAuditor" :disabled="loading">
                        <v-icon left small>event_available</v-icon> Programmazione audit
                    </v-tab>
                </v-tabs>
                <v-divider />

                <v-tabs-items v-model="activeTab">
                    <!-- Tab 1: Dati Utente -->
                    <v-tab-item>
                        <dati-utente :cliente="Cliente" :readonly="isAuditor" />
                    </v-tab-item>

                    <!-- Tab 2: Dati Certificazioni -->
                    <v-tab-item>
                        <dati-certificazioni :cliente="Cliente" @update="updateCertificazioni" />
                    </v-tab-item>

                    <!-- Tab 3: Dati FSC-G -->
                    <v-tab-item>
                        <dati-gruppo :cliente="Cliente" :ui-state="uiStateFSC" tipo-certificazione="FSC"
                            @update:ui-state="uiStateFSC = $event" />
                    </v-tab-item>

                    <!-- Tab 4: Dati PEFC-G -->
                    <v-tab-item>
                        <dati-gruppo :cliente="Cliente" :ui-state="uiStatePEFC" tipo-certificazione="PEFC"
                            @update:ui-state="uiStatePEFC = $event" />
                    </v-tab-item>

                    <!-- Tab 5: Altri Dati (non disponibile per l'auditor: password/stato/registri restano gestiti solo dall'amministratore) -->
                    <v-tab-item v-if="!isAuditor">
                        <altri-dati :cliente="Cliente" :modifica="modifica" :psw="psw" @update:psw="psw = $event"
                            @genera-link="generaLink" />
                    </v-tab-item>

                    <!-- Tab 6: Programmazione Audit -->
                    <v-tab-item v-if="!isAuditor">
                        <programmazione-audit v-model="programmazioneAudit" :company-code="Cliente.companyCode"
                            :disabled="!modifica" />
                    </v-tab-item>
                </v-tabs-items>
            </v-card-text>

            <v-divider style="flex: 0 0 auto;" />

            <v-footer class="pa-2" height="auto" style="flex: 0 0 auto;">
                <v-spacer />
                <v-btn :disabled="loading" color="primary" text @click="chiudi()">
                    <v-icon left small>close</v-icon> Annulla
                </v-btn>
                <v-btn :disabled="loading" :loading="loading" color="primary" @click="salva()">
                    <v-icon left small>save</v-icon> Salva
                </v-btn>
            </v-footer>
        </v-card>

        <!-- Hidden element for clipboard copy -->
        <div ref="contentCopy" style="position: absolute; left: -9999px"></div>
    </v-dialog>
</template>

<script>
import {
    set_cliente,
    get_companyCode,
    get_datiCliente,
    get_programmazione_audit,
    modifica_programmazione_audit,
} from "@/api/api.clienti.js";
import { get_link_reset_password } from "@/api/api.auth.js";
import mixinCliente from "@/utils/cliente/mixinCliente";

// Componenti figli
import DatiUtente from "@/components/cliente/DatiUtente.vue";
import DatiCertificazioni from "@/components/cliente/DatiCertificazioni.vue";
import DatiGruppo from "@/components/cliente/DatiGruppo.vue";
import AltriDati from "@/components/cliente/AltriDati.vue";
import ProgrammazioneAudit from "@/components/dialog/ProgrammazioneAudit.vue";

// Stato iniziale/di reset per i pannelli FSC-G e PEFC-G (evita di duplicare lo stesso oggetto in data() e resetCampi())
function creaUiStateVuoto() {
    return {
        gruppo: "",
        terzisti: null,
        rischio: null,
        tipologia: {
            verniciatore: false,
            commercialista: false,
            lamellatore: false,
        },
    };
}

// Configurazione certificazioni
const CERT_CONFIG = {
    FSCS: { gruppo: "S", campiPreventivo: ["preventivoFSCS", "mantenimentoFSCS"] },
    FSCG: { gruppo: "G", campiPreventivo: ["preventivoFSCG", "mantenimentoFSCG"] },
    PEFC: { gruppo: "S", campiPreventivo: ["preventivoPEFC", "mantenimentoPEFC"] },
    PEFCG: { gruppo: "G", campiPreventivo: ["preventivoPEFCG", "mantenimentoPEFCG"] },
    FPC: { gruppo: "S", campiPreventivo: ["preventivoFPC", "mantenimentoFPC"] },
    ISO: { gruppo: "S", campiPreventivo: ["preventivoISO", "mantenimentoISO"] },
};

export default {
    name: "DettaglioCliente",

    components: {
        DatiUtente,
        DatiCertificazioni,
        DatiGruppo,
        AltriDati,
        ProgrammazioneAudit,
    },

    mixins: [mixinCliente],

    props: {
        dialog: { type: Boolean, default: false },
        modifica: { type: Boolean, default: false },
        cliente: { type: Object, default: null },
    },

    data: () => ({
        loading: false,
        activeTab: 0,
        psw: "",
        Cliente: {},
        programmazioneAudit: [],

        // UI State per i pannelli FSC-G e PEFC-G
        uiStateFSC: creaUiStateVuoto(),
        uiStatePEFC: creaUiStateVuoto(),
    }),

    computed: {
        certificazioniAttive() {
            return Object.keys(CERT_CONFIG)
                .filter((key) => this.Cliente[`certificazione${key}`])
                .join("|");
        },

        isAuditor() {
            return this.$store.getters["user/getRole"] === "auditor";
        },

        titoloScheda() {
            return this.modifica && this.Cliente.ragioneSociale
                ? `Scheda Cliente - ${this.Cliente.ragioneSociale}`
                : "Scheda Cliente";
        },
    },

    watch: {
        async dialog(newVal) {
            if (newVal) {
                const oggi = new Date().toISOString().substr(0, 10);
                this.Cliente.dataIngresso = oggi;
                this.Cliente.dataRimozione = oggi;

                if (!this.modifica) {
                    await this.caricaNuovoCliente();
                } else {
                    await this.caricaClienteEsistente();
                }
            }
        },

        certificazioniAttive() {
            // Durante il caricamento dei dati salvati il cambio di certificazioni non e' una scelta dell'utente:
            // senza questa guardia il reset dei dati gruppo azzerava responsabili/applicazione appena letti.
            if (this.loading) return;
            this.aggiornaCertificazioniAutomatiche();
        },
    },

    created() {
        this.Cliente = this.getDatiClienteInit();
    },

    methods: {
        // ===== GESTIONE DIALOGO =====
        chiudi(salvato = false) {
            this.psw = "";
            this.resetCampi();
            this.$emit("chiudiCliente", salvato);
        },

        // ===== CARICAMENTO DATI =====
        async caricaNuovoCliente() {
            this.loading = true;
            try {
                const res = await get_companyCode();
                this.Cliente = res.data;
                this.Cliente.enabled = true;
                this.programmazioneAudit = [];
            } catch (error) {
                this.$toast.alert("Errore nel caricamento del codice cliente");
                console.error(error);
            } finally {
                this.loading = false;
            }
        },

        async caricaClienteEsistente() {
            this.loading = true;
            try {
                const res = await get_datiCliente({ companyCode: this.cliente.companyCode });
                this.Cliente = res.data;
                this.Cliente.modificato = true;

                // Imposta gli stati UI da dati esistenti
                this.inizializzaUIStateDaDati();
                await this.caricaProgrammazioni();
            } catch (error) {
                this.$toast.alert("Errore nel caricamento dei dati cliente");
                console.error(error);
            } finally {
                this.loading = false;
            }
        },

        inizializzaUIStateDaDati() {
            // FSC
            this.uiStateFSC.gruppo = this.Cliente.gruppo || "";
            if (this.Cliente.certificazioneFSCG) this.uiStateFSC.gruppo = 'G';
            if (this.Cliente.certificazioneFSC) this.uiStateFSC.gruppo = 'S';
            this.uiStateFSC.terzisti = this.Cliente.terzisti;
            this.uiStateFSC.rischio = this.Cliente.rischio;
            this.parseTipologia(this.Cliente.tipologia, this.uiStateFSC.tipologia);

            // PEFC
            this.uiStatePEFC.gruppo = this.Cliente.gruppoPEFC || "";
            if (this.Cliente.certificazionePEFCG) this.uiStatePEFC.gruppoPEFC = 'G';
            if (this.Cliente.certificazionePEFC) this.uiStatePEFC.gruppoPEFC = 'S';
            this.uiStatePEFC.terzisti = this.Cliente.terzistiPEFC;
            this.uiStatePEFC.rischio = this.Cliente.rischioPEFC;
            this.parseTipologia(this.Cliente.tipologiaPEFC, this.uiStatePEFC.tipologia);
        },

        parseTipologia(tipologiaString, tipologiaObj) {
            if (!tipologiaString) return;
            tipologiaObj.verniciatore = tipologiaString.includes("Verniciatore");
            tipologiaObj.commercialista = tipologiaString.includes("Commercialista");
            tipologiaObj.lamellatore = tipologiaString.includes("Lamellatore");
        },

        // ===== SALVATAGGIO =====
        async salva() {
            this.loading = true;

            // L'auditor non vede/modifica la password (tab "Altri Dati" nascosto): nessuna validazione da fare
            if (!this.isAuditor) {
                // Validazione password per nuovi clienti
                if (!this.modifica) {
                    if (!this.validaPassword()) {
                        this.loading = false;
                        return;
                    }
                } else {
                    // Per clienti esistenti, verifica corrispondenza password
                    if (this.Cliente.passwordHash !== this.psw) {
                        this.$toast.alert("Le password non coincidono!");
                        this.loading = false;
                        return;
                    }
                }
            }

            // Prepara i dati per il salvataggio
            this.preparaDatiPerSalvataggio();

            // Salva cliente
            const salvataggioOk = await this.salvaCliente();
            if (!salvataggioOk) {
                this.loading = false;
                return;
            }

            // Salva programmazione audit
            await this.salvaProgrammazione();

            this.loading = false;
            this.chiudi(false);
        },

        validaPassword() {
            if (this.Cliente.passwordHash !== this.psw) {
                this.$toast.alert("Le password non coincidono!");
                return false;
            }
            return true;
        },

        preparaDatiPerSalvataggio() {
            // Username = Ragione Sociale
            this.Cliente.userName = this.Cliente.ragioneSociale;

            // Certificazioni
            this.costruisciStringaCertificazioni();

            // Dati FSC
            this.Cliente.gruppo = this.uiStateFSC.gruppo;
            if (this.Cliente.certificazioneFSCG) this.Cliente.gruppo = 'G';
            if (this.Cliente.certificazioneFSC) this.Cliente.gruppo = 'S';
            this.Cliente.terzisti = this.uiStateFSC.terzisti === true;
            this.Cliente.rischio = this.uiStateFSC.rischio === true;
            this.Cliente.tipologia = this.costruisciTipologia(this.uiStateFSC.tipologia);

            // Dati PEFC
            this.Cliente.gruppoPEFC = this.uiStatePEFC.gruppo;
            if (this.Cliente.certificazionePEFCG) this.Cliente.gruppoPEFC = 'G';
            if (this.Cliente.certificazionePEFC) this.Cliente.gruppoPEFC = 'S';

            this.Cliente.terzistiPEFC = this.uiStatePEFC.terzisti === true;
            this.Cliente.rischioPEFC = this.uiStatePEFC.rischio === true;
            this.Cliente.tipologiaPEFC = this.costruisciTipologia(this.uiStatePEFC.tipologia);

            // Modificato
            this.Cliente.modificato = this.modifica;
        },

        costruisciTipologia(tipologiaObj) {
            const tipologie = [];
            if (tipologiaObj.verniciatore) tipologie.push("Verniciatore");
            if (tipologiaObj.commercialista) tipologie.push("Commercialista");
            if (tipologiaObj.lamellatore) tipologie.push("Lamellatore");
            return tipologie.join(", ");
        },

        costruisciStringaCertificazioni() {
            const certificazioni = [];

            if (this.Cliente.certificazioneFSCS) certificazioni.push("FSC (singola)");
            if (this.Cliente.certificazioneFSCG) certificazioni.push("FSC (gruppo)");
            if (this.Cliente.certificazionePEFC) certificazioni.push("PEFC");
            if (this.Cliente.certificazionePEFCG) certificazioni.push("PEFCG");
            if (this.Cliente.certificazioneFPC) certificazioni.push("FPC");
            if (this.Cliente.certificazioneISO) certificazioni.push("ISO");

            this.Cliente.certificazione = certificazioni.join(", ");
        },

        async salvaCliente() {
            try {
                const res = await set_cliente(this.Cliente);
                if (res.data === "OK") {
                    this.$toast.info("Salvataggio dati completato!");
                    return true;
                } else {
                    this.$toast.alert("Errore durante il salvataggio dei dati!");
                    return false;
                }
            } catch (error) {
                this.$toast.alert("Errore durante il salvataggio dei dati!");
                console.error(error);
                return false;
            }
        },

        async salvaProgrammazione() {
            try {
                const par = {
                    CodiceAzienda: this.Cliente.companyCode,
                    Programmazione: this.programmazioneAudit,
                };
                const res = await modifica_programmazione_audit(par, "nuovo");
                if (res.data === "OK") {
                    this.$toast.info("Dati programmazione salvati");
                } else {
                    this.$toast.alert("Errore durante il salvataggio della programmazione");
                }
            } catch (error) {
                this.$toast.alert("Errore durante il salvataggio della programmazione");
                console.error(error);
            }
        },

        // ===== PROGRAMMAZIONE AUDIT =====
        async caricaProgrammazioni() {
            try {
                const par = {
                    companyCode: this.cliente.companyCode,
                    userId: this.cliente.userId,
                };
                const res = await get_programmazione_audit(par);
                this.programmazioneAudit = res.data.map((item) => ({
                    ...item,
                    data: item.data ? item.data.substr(0, 10) : null,
                    programmazione: item.programmazione ? item.programmazione.substr(0, 10) : null,
                }));
            } catch (error) {
                console.error("Errore caricamento programmazioni:", error);
            }
        },

        // ===== GESTIONE CERTIFICAZIONI =====
        updateCertificazioni() {
            this.aggiornaCertificazioniAutomatiche();
        },

        aggiornaCertificazioniAutomatiche() {
            const certificazioniAttive = Object.keys(CERT_CONFIG).filter(
                (key) => this.Cliente[`certificazione${key}`]
            );

            const haGruppo = certificazioniAttive.some(
                (key) => CERT_CONFIG[key].gruppo === "G"
            );
            const haSingola = certificazioniAttive.some(
                (key) => CERT_CONFIG[key].gruppo === "S"
            );

            // Imposta gruppo automaticamente
            if (haGruppo) {
                this.uiStateFSC.gruppo = "G";
            } else if (haSingola) {
                this.uiStateFSC.gruppo = "S";
            } else {
                this.uiStateFSC.gruppo = "";
            }

            // Resetta campi per certificazioni non attive
            Object.keys(CERT_CONFIG).forEach((key) => {
                if (!this.Cliente[`certificazione${key}`]) {
                    CERT_CONFIG[key].campiPreventivo.forEach((campo) => {
                        this.Cliente[campo] = "";
                    });
                }
            });

            // Se deseleziono FSC-G, resetta dati gruppo
            if (!this.Cliente.certificazioneFSCG) {
                this.resetDatiGruppo("FSC");
            }

            // Se deseleziono PEFC-G, resetta dati gruppo
            if (!this.Cliente.certificazionePEFCG) {
                this.resetDatiGruppo("PEFC");
            }

            this.costruisciStringaCertificazioni();
        },

        resetDatiGruppo(tipo) {
            const uiState = tipo === "FSC" ? this.uiStateFSC : this.uiStatePEFC;

            // Reset UI state (mutazione in-place per preservare la reattivita' dell'oggetto esistente)
            Object.assign(uiState, creaUiStateVuoto());

            // Reset campi cliente
            if (tipo === "FSC") {
                this.Cliente.applicazione = "";
                this.Cliente.respCoc = "";
                this.Cliente.respFPC = "";
                this.Cliente.dataRimozione = "";
                this.Cliente.dataIngresso = "";
            }
        },

        // ===== RESET PASSWORD =====
        async generaLink() {
            try {
                const par = {
                    Username: this.Cliente.userId.toString(),
                    Mail: this.Cliente.eMail,
                };
                const res = await get_link_reset_password(par);

                if (res.data.toString().startsWith("###")) {
                    this.$toast.alert(
                        "Errore durante la generazione del link: " +
                        res.data.replace("###", "")
                    );
                } else {
                    this.$refs.contentCopy.innerText = res.data;
                    this.$refs.contentCopy.focus();
                    await navigator.clipboard.writeText(res.data);
                    this.$toast.info("Testo copiato negli appunti");
                }
            } catch (e) {
                this.$toast.alert("Errore durante la generazione del link");
                console.error(e);
            }
        },

        // ===== RESET =====
        resetCampi() {
            this.activeTab = 0;
            this.Cliente = this.getDatiClienteInit();
            this.programmazioneAudit = [];
            this.uiStateFSC = creaUiStateVuoto();
            this.uiStatePEFC = creaUiStateVuoto();
        },
    },
};
</script>